<!--
  An editor for utterances, and their synonyms and entities

  Props:
  - value: the utterance value
      ex: "(#hello:synonymd|) my name is @name:entityid"
  - segments: array of segments, generated by ElUtteranceInput and synced
      ex: [{
            key: "0.345345", // random
            type: 'entity',
            text: 'name',
            ref: 'entityid',
          }, ...]
  - prop: the prefix for el-form-item
  - required: required
  - rules: extra rules for the utterance
  - variableSuggestions: autocomplete for @var variables
  - itemsOptions: all available entities and synonyms
  - active: if should open slidedown
  - newInput: for a new input, without slidedown
  - newFocus: if should focus on creation
  - placeholder: the text to display for an empty utterance

  Emits:
  - @input(value): value changed
  - @update:segments(segments): segments changed
  - @update:text(key, text): the name of the segment "key" has chaned
  - @update:ref(key, ref): the entityid of the segment "key" has chaned
  - @activate(active): if should activate the item
  - @delete: should delete the item
-->

<template>
  <div @click="handleActivate">
    <comparator-filter>
      <template v-slot="{ compare, index }">
        <div>
          <comparable :prop="x => stringify(x.value)"
            :required="!newInput"
            :rules="computedRules">
            <template v-slot="{ attrs, status, view, compare }">
              <el-form-small-item :class="'comparable-' + status"
                :prop="view && prop"
                v-bind="attrs"
                field="utterance">
                <el-button-group>
                  <el-tooltip v-if="newInput"
                    effect="light" placement="bottom-start" :tabindex="-1">
                    <template slot="content">
                      <slot name="tooltip">
                        Input templates can contain <code>@var</code> <em>entity</em> to parse the user input,<br/>
                        alternative input words <code>(hello|good morning|)</code>,<br/>
                        and <em>synonyms</em> can be used on selected text.<br/>
                        Example: <code>(hello|good morning|) what time is it in @city (please|) ?</code>
                      </slot>
                    </template>
                    <el-button type="info" style="padding: 12px"
                      icon="el-icon-info" :tabindex="-1"></el-button>
                  </el-tooltip>
                  <!-- button to open the slidedown -->
                  <el-tooltip v-else
                    effect="light" placement="left" :tabindex="-1"
                    :content="'# ' + index">
                    <el-button type="info" style="padding: 12px"
                      :disabled="compare"
                      :icon="!formValid ? 'el-icon-error' : !hasEntities ? 'el-icon-minus' : active ? 'el-icon-caret-top' : 'el-icon-caret-bottom'"
                      @click.stop="handleActivate(!active && formValid)"
                      :tabindex="-1">
                    </el-button>
                  </el-tooltip>
                  <!-- utterance input -->
                  <el-utterance-autocomplete
                    ref="input"
                    v-on="$listeners"
                    :disabled="compare"
                    :placeholder="placeholder"
                    :value="value"
                    :segments="copySegments"
                    :local-variables="variableSuggestions"
                    @update:segments="handleSegments"
                    @selected="handleSelected"
                    @input="handleInput">
                    <template slot="prepend" v-if="$slots.prepend">
                      <slot name="prepend"></slot>
                    </template>
                    <template slot="append" v-if="$slots.append">
                      <slot name="append"></slot>
                    </template>
                    <template slot="prefix" v-if="$slots.prefix">
                      <slot name="prefix"></slot>
                    </template>
                    <template slot="suffix" v-if="$slots.suffix">
                      <slot name="suffix"></slot>
                    </template>
                  </el-utterance-autocomplete>
                  <!-- delete button -->
                  <el-button
                    :disabled="newInput || compare"
                    :icon="newInput ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
                    @click="handleDelete"
                    :tabindex="-1">
                  </el-button>
                </el-button-group>
              </el-form-small-item>
            </template>
          </comparable>
          <!-- slidedown -->
          <el-collapse-transition v-if="!newInput && (activated || active)">
            <el-form-sub label-width="120px"
              v-show="opened"
              :prop="prop"
              :model="{entities}"
              @valid="val => formValid = val">
              <fieldset>
                <legend>Entities</legend>
                <!-- "table" headers -->
                <input-headers-group>
                  <input-header>Entity name</input-header>
                  <input-header>Entity type</input-header>
                  <input-header width="39px"></input-header>
                  <input-header width="56px"></input-header>
                </input-headers-group>
                <!-- all the entities and synonyms -->
                <el-form-errors
                  v-for="(entity, index) in entities" :key="entity.key">
                  <el-button-group style="width: 100%">
                    <!-- name input -->
                    <el-form-small-item
                      :prop="'entities.'+index+'.text'"
                      :required="!entity.temporary"
                      field="text"
                      :rules="entity.type === 'entity' ? [VariableValidator, duplicateRule(entity.key)] : !entity.temporary ? ReadableValidator : null">
                      <el-input
                        :value="entity.text"
                        :disabled="compare"
                        @input="(val) => handleText(entity, val)"
                        :tabindex="opened ? '0' : '-1'">
                      </el-input>
                    </el-form-small-item>
                    <!-- synonym or entity select -->
                    <el-form-small-item
                      :prop="'entities.'+index+'.ref'"
                      :required="!entity.temporary"
                      field="type">
                      <el-filterable-entity-select
                        :value="entity.ref"
                        :pre-filter="entity.text"
                        :disabled="compare"
                        @input="(val) => handleRef(entity, val)"
                        :options="itemsOptions[entity.type]"
                        :type="entity.type"
                        :value-field="entity.type"
                        label-field="name"
                        :tabindex="opened ? 0 : -1">
                        <template slot="info" slot-scope="{item}">{{
                          item && !item.local ? '(global)' : null
                        }}</template>
                      </el-filterable-entity-select>
                    </el-form-small-item>
                    <router-link v-if="(itemsLinks[entity.type] || {})[entity.ref]"
                      style="padding: 12px"
                      :to="itemsLinks[entity.type][entity.ref]"
                      target="_blank"
                      class="el-button"
                      :tabindex="-1">
                      <i class="el-icon-link"></i>
                    </router-link>
                    <el-button v-else disabled
                      style="padding: 12px"
                      icon="el-icon-link"
                      :tabindex="-1">
                    </el-button>
                    <!-- add or delete button for synonyms -->
                    <el-button
                      :icon="entity.temporary ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
                      :disabled="compare || entity.temporary || entity.type !== 'synonym'"
                      @click="$refs.input.unreference(entity.key)"
                      :tabindex="-1">
                    </el-button>
                  </el-button-group>
                </el-form-errors>
              </fieldset>
            </el-form-sub>
          </el-collapse-transition>
        </div>
      </template>
    </comparator-filter>
  </div>
</template>

<script>
import {
  PreInputValidator,
  InputValidator,
  VariableValidator,
  ReadableValidator,
} from '../../utils/validators.js'

import ElUtteranceAutocomplete from '../element/ElUtteranceAutocomplete.vue'
import ElFilterableEntitySelect from '../element/ElFilterableEntitySelect.vue'
import { stringify } from '../element/ElUtteranceInput.vue'

export default {
  name: 'UtteranceEditor',
  components: {
    ElUtteranceAutocomplete,
    ElFilterableEntitySelect,
  },
  props: {
    value: String,
    // array of segments, generated by ElUtteranceInput and synced
    segments: Array,
    itemsOptions: Object, // all available entities
    itemsLinks: Object, // the link to all the acessible items
    active: { // if should open slidedown
      type: Boolean,
      default: true,
    },
    newInput: Boolean, // for a new input, without slidedown
    newFocus: Boolean, // if should focus on creation
    placeholder: String, // the text to display for an empty utterance

    prop: String, // the prefix for el-form-item
    rules: [Object, Array], // extra rules for the utterance
    variableSuggestions: Array, // autocomplete for @var variables
  },
  data () {
    return {
      copySegments: [], // a copy of the ElUtteranceInput.segments
      selection: null, // from ElUtteranceInput, the selected text (for synonyms)
      formValid: true, // if the form is valid
      activated: false, // optimisation to avoid loading the slidedown
      VariableValidator, // validator for the entity name
      ReadableValidator, // validator for the synonym text
      stringify, // ElUtteranceInput.stringify, for comparable entity values
      shouldActivate: true, // if should activate when an entity appears
      lastValue: '', // the last value return by the utterance input
    }
  },
  computed: {
    // rules for the utterances
    computedRules () {
      return [ PreInputValidator, InputValidator ].concat(this.rules || [])
    },
    // an array of all the the entities and synonyms segments
    // with eventually the temporary synonym from the selection
    entities () {
      let out = this.copySegments.filter(seg => seg.type !== 'text')
      if (this.selection) out.push(this.selection)
      return out
    },
    hasEntities () {
      return !!this.entities.length
    },
    // if should open the slider
    opened () {
      return this.activated && this.active && this.hasEntities || !this.formValid
    },
  },
  methods: {
    // focus the utterance input
    focus () {
      this.$refs.newInput.focus()
      this.handleActivate()
    },
    // blur the utterance input
    blur () {
      this.$refs.input.blur()
    },
    // update "shouldActivate" if the value is new
    handleInput (val) {
      this.shouldActivate = val !== this.value
      this.lastValue = val
      this.$emit('input', val)
    },
    // segments are updated by the utterance input
    handleSegments (val) {
      this.copySegments = val
    },
    // something has been selected in the utterance input
    // create a temporary segment for a new synonym
    handleSelected (val) {
      this.selection = val && {
        ...val,
        ref: '',
        type: 'synonym',
        temporary: true,
      }
    },
    // emit activate
    handleActivate (activate) {
      if (this.newInput) return
      this.$emit('activate', activate !== false)
    },
    // emit delete
    handleDelete () {
      this.$emit('delete')
    },
    // the text of an entity or a synonym has been changed
    // emit if an entity, or just change the input if a synonym
    handleText (entity, text) {
      if (entity.type === 'entity') {
        this.$emit('update:text', entity.key, text)
      } else this.setText(entity.key, text, entity.temporary)
    },
    // change the text of a segment
    setText (key, val, temp) {
      // the temporary segment
      if (temp) {
        this.selection.text = val
      } else { // other segment, update the utterance input
        this.$refs.input.setText(key, val)
      }
    },
    // the entityid or synonymid has been changed
    // emit if an entity, or just change the input if a synonym
    handleRef (entity, ref) {
      if (entity.type === 'entity') {
        this.$emit('update:ref', entity.key, ref)
      } else this.setRef(entity.key, ref, entity.temporary)
    },
    // change the entityid or synonymid or a segment
    setRef (key, val, temp) {
      val = val || null
      // the temporary segment, insert a new synonym in the utterance input
      if (temp) {
        let sel = this.selection
        sel.ref = val
        if (!val) return
        this.selection = null
        this.$refs.input.insert(sel)
      } else { // other segment, update the utterance input
        this.$refs.input.setRef(key, val)
      }
    },
    // a validator specifically for checking duplicate entity name
    duplicateRule (key) {
      return {
        validator: (rule, value, callback) => {
          callback(this.checkDuplicateName(value, key))
        },
      }
    },
    // used by duplicateRule, look for duplicate entity name
    checkDuplicateName (name, key) {
      for (let seg of this.entities) {
        if (seg.text === name && seg.type === 'entity' && seg.key !== key) {
          return new Error('Variable defined multiple times')
        }
      }
    },
  },
  // init copySegments and activated
  created () {
    this.copySegments = this.segments || []
    this.activated = this.active
  },
  watch: {
    // when eventually active, switch activated to true
    active (active) {
      if (active) this.$nextTick(() => { this.activated = true })
    },
    // update "shouldActivate" if the value is not new
    value (val) {
      this.shouldActivate = val === this.lastValue
    },
    // activate if it has entities and "shouldActivate"
    hasEntities (val) {
      if (val && this.shouldActivate) {
        this.$emit('activate', true)
      }
    },
  },
  // focus if newFocus
  mounted () {
    if (this.newFocus) this.focus()
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  .utterance-editor-header {
    display: flex;
    color: $--color-info;
    font-weight: bold;
    margin: $input-header-vpadding $input-header-hmargin 0;
    border-bottom: 1px solid $--border-color-base;
  }
  .utterance-editor-name {
    display: block;
    flex: $field-flex;
    padding: $input-header-padding;
    padding-right: $input-header-hpadding - $input-header-hmargin;
  }
  .utterance-editor-type {
    display: block;
    flex: $field-flex;
    padding: $input-header-padding;
    border-left: 1px solid $--border-color-base;
  }
  .utterance-editor-link {
    display: block;
    width: 38px - 1px;
  }
  .utterance-editor-delete {
    display: block;
    width: 55px - $input-header-hmargin;
    border-left: 1px solid $--border-color-base;
  }
</style>
